/**
 * Contact Form Utilities
 */

export const SUBJECT_OPTIONS = [
  'General Inquiry',
  'Domain Purchase',
  'Technical Support',
  'Billing Question',
  'Partnership Opportunity',
  'Other',
] as const;

export type SubjectOption = (typeof SUBJECT_OPTIONS)[number];

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  honeypot?: string;
}

const DRAFT_KEY = 'contact_form_draft';
const SUBMISSION_KEY = 'last_submission';
const THROTTLE_MS = 60000; // 1 minute

export interface ValidationErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export interface SubmissionState {
  status: 'idle' | 'submitting' | 'success' | 'error' | 'throttled';
  message?: string;
}

export function validateForm(data: ContactFormData): ValidationErrors {
  const errors: ValidationErrors = {};
  
  if (!data.name || data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (!data.email || !data.email.includes('@')) {
    errors.email = 'Valid email is required';
  }

  if (!data.subject || data.subject.trim().length < 3) {
    errors.subject = 'Subject must be at least 3 characters';
  }

  if (!data.message || data.message.trim().length < 5) {
    errors.message = 'Message must be at least 5 characters';
  }

  return errors;
}

export function saveDraft(data: Partial<ContactFormData>): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save draft:', error);
  }
}

export function loadDraft(): Partial<ContactFormData> | null {
  try {
    const draft = localStorage.getItem(DRAFT_KEY);
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Failed to load draft:', error);
    return null;
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (error) {
    console.error('Failed to clear draft:', error);
  }
}

export function setSubmissionTimestamp(): void {
  try {
    localStorage.setItem(SUBMISSION_KEY, Date.now().toString());
  } catch (error) {
    console.error('Failed to set submission timestamp:', error);
  }
}

export function isSubmissionThrottled(): boolean {
  try {
    const lastSubmission = localStorage.getItem(SUBMISSION_KEY);
    if (!lastSubmission) return false;

    const elapsed = Date.now() - parseInt(lastSubmission, 10);
    return elapsed < THROTTLE_MS;
  } catch (error) {
    console.error('Failed to check throttle:', error);
    return false;
  }
}

export function getThrottleTimeRemaining(): number {
  try {
    const lastSubmission = localStorage.getItem(SUBMISSION_KEY);
    if (!lastSubmission) return 0;

    const elapsed = Date.now() - parseInt(lastSubmission, 10);
    const remaining = THROTTLE_MS - elapsed;
    return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
  } catch (error) {
    console.error('Failed to get throttle time:', error);
    return 0;
  }
}

export function checkHoneypot(value?: string): boolean {
  // Return true ONLY when bot filled the hidden field
  return !!(value && value.trim().length > 0);
}

export function createSubmissionPayload(data: ContactFormData) {
  return {
    name: data.name.trim(),
    email: data.email.trim().toLowerCase(),
    subject: data.subject.trim(),
    message: data.message.trim(),
    timestamp: new Date().toISOString(),
  };
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
